/**
 * QuoteMaterialsCPQ.js
 * Componente per la gestione CPQ dei materiali con prezzi, margini e warning
 */

import { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
    Trash,
    Pencil,
    Warning,
    TrendUp,
    TrendDown,
    CurrencyEur,
    Percent,
    Package,
    Lightning,
    Cube,
    BatteryFull,
    CaretDown,
    CaretUp,
    Check,
    X
} from '@phosphor-icons/react';

// Formattazione valuta
const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
};

// Formattazione percentuale
const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(1)}%`;
};

// Icona categoria
const CategoryIcon = ({ category }) => {
    switch (category) {
        case 'MODULI': return <Package className="w-4 h-4 text-amber-500" />;
        case 'INVERTER':
        case 'INVERTER_IBRIDO': return <Lightning className="w-4 h-4 text-blue-500" />;
        case 'STORAGE': return <BatteryFull className="w-4 h-4 text-green-500" />;
        default: return <Cube className="w-4 h-4 text-zinc-400" />;
    }
};

// Badge margine con colore
const MarginBadge = ({ margin, percent }) => {
    let bgColor = 'bg-green-100 text-green-700';
    let Icon = TrendUp;
    
    if (margin < 0) {
        bgColor = 'bg-red-100 text-red-700';
        Icon = TrendDown;
    } else if (percent < 10) {
        bgColor = 'bg-amber-100 text-amber-700';
        Icon = Warning;
    }
    
    return (
        <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${bgColor}`}>
            <Icon className="w-3 h-3" />
            <span>{formatCurrency(margin)}</span>
            <span className="opacity-70">({formatPercent(percent)})</span>
        </div>
    );
};

// Riga materiale editabile
const MaterialRow = ({ line, index, isEditable, onUpdate, onDelete, canViewMargins }) => {
    const [editing, setEditing] = useState(false);
    const [editValues, setEditValues] = useState({
        quantity: line.quantity || 1,
        customer_unit_price: line.customer_unit_price || 0,
        discount_percent: line.discount_percent || 0
    });
    
    const handleSave = async () => {
        await onUpdate(index, {
            quantity: parseFloat(editValues.quantity),
            customer_unit_price: parseFloat(editValues.customer_unit_price),
            discount_percent: parseFloat(editValues.discount_percent)
        });
        setEditing(false);
    };
    
    const handleCancel = () => {
        setEditValues({
            quantity: line.quantity || 1,
            customer_unit_price: line.customer_unit_price || 0,
            discount_percent: line.discount_percent || 0
        });
        setEditing(false);
    };
    
    const warnings = line.warnings || [];
    const hasWarnings = warnings.length > 0;
    
    return (
        <TableRow className={!line.included ? 'opacity-50' : ''}>
            {/* Incluso */}
            <TableCell className="w-10">
                <Switch
                    checked={line.included !== false}
                    onCheckedChange={(checked) => onUpdate(index, { included: checked })}
                    disabled={!isEditable}
                />
            </TableCell>
            
            {/* Componente */}
            <TableCell>
                <div className="flex items-start gap-2">
                    <CategoryIcon category={line.category} />
                    <div>
                        <div className="font-medium text-sm">{line.description}</div>
                        <div className="text-xs text-zinc-500">{line.brand} {line.model}</div>
                    </div>
                    {hasWarnings && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <Warning className="w-4 h-4 text-amber-500" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <ul className="text-xs">
                                        {warnings.map((w, i) => <li key={`w-${i}-${(w || '').slice(0, 24)}`}>• {w}</li>)}
                                    </ul>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            </TableCell>
            
            {/* Quantità */}
            <TableCell className="w-20">
                {editing ? (
                    <Input
                        type="number"
                        className="w-16 h-8 text-sm"
                        value={editValues.quantity}
                        onChange={(e) => setEditValues({...editValues, quantity: e.target.value})}
                    />
                ) : (
                    <span>{line.quantity} {line.unit}</span>
                )}
            </TableCell>
            
            {/* Costo Acquisto - Solo se può vedere margini */}
            {canViewMargins && (
                <TableCell className="text-right text-zinc-500 text-sm">
                    <div>{formatCurrency(line.purchase_unit_price)}</div>
                    <div className="text-xs">{formatCurrency(line.purchase_total)}</div>
                </TableCell>
            )}
            
            {/* Prezzo Cliente */}
            <TableCell className="text-right">
                {editing ? (
                    <Input
                        type="number"
                        step="0.01"
                        className="w-24 h-8 text-sm"
                        value={editValues.customer_unit_price}
                        onChange={(e) => setEditValues({...editValues, customer_unit_price: e.target.value})}
                    />
                ) : (
                    <div>
                        <div className="font-medium">{formatCurrency(line.customer_unit_price)}</div>
                    </div>
                )}
            </TableCell>
            
            {/* Sconto */}
            <TableCell className="w-20">
                {editing ? (
                    <div className="flex items-center gap-1">
                        <Input
                            type="number"
                            step="0.1"
                            className="w-14 h-8 text-sm"
                            value={editValues.discount_percent}
                            onChange={(e) => setEditValues({...editValues, discount_percent: e.target.value})}
                        />
                        <span className="text-xs">%</span>
                    </div>
                ) : (
                    line.discount_percent > 0 ? (
                        <Badge variant="secondary" className="text-xs">
                            -{line.discount_percent}%
                        </Badge>
                    ) : null
                )}
            </TableCell>
            
            {/* Totale Cliente */}
            <TableCell className="text-right font-semibold">
                {formatCurrency(line.customer_total)}
            </TableCell>
            
            {/* Margine - Solo se può vedere */}
            {canViewMargins && (
                <TableCell>
                    <MarginBadge margin={line.margin_amount} percent={line.margin_percent} />
                </TableCell>
            )}
            
            {/* Azioni */}
            <TableCell className="w-24">
                {isEditable && (
                    <div className="flex items-center gap-1">
                        {editing ? (
                            <>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleSave}>
                                    <Check className="w-4 h-4 text-green-600" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleCancel}>
                                    <X className="w-4 h-4 text-red-600" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(true)}>
                                    <Pencil className="w-4 h-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => onDelete(index)}>
                                    <Trash className="w-4 h-4" />
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </TableCell>
        </TableRow>
    );
};

// Riepilogo Margini
const MarginSummary = ({ quote, canViewMargins }) => {
    if (!canViewMargins) return null;
    
    return (
        <Card className="border-2 border-dashed border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                    <TrendUp className="w-4 h-4 text-blue-600" />
                    Riepilogo Margini (Uso Interno)
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-4 gap-4 text-sm">
                    {/* Materiali */}
                    <div className="space-y-1">
                        <div className="text-xs text-zinc-500 uppercase">Materiali</div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Costo:</span>
                            <span>{formatCurrency(quote.purchase_materials)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Vendita:</span>
                            <span className="font-medium">{formatCurrency(quote.total_materials)}</span>
                        </div>
                        <div className="flex justify-between pt-1 border-t">
                            <span className="text-zinc-500">Margine:</span>
                            <span className={quote.margin_materials >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {formatCurrency(quote.margin_materials)} ({formatPercent(quote.margin_materials_percent)})
                            </span>
                        </div>
                    </div>
                    
                    {/* Installazione */}
                    <div className="space-y-1">
                        <div className="text-xs text-zinc-500 uppercase">Installazione</div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Costo:</span>
                            <span>{formatCurrency(quote.purchase_installation)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Vendita:</span>
                            <span className="font-medium">{formatCurrency(quote.total_installation)}</span>
                        </div>
                        <div className="flex justify-between pt-1 border-t">
                            <span className="text-zinc-500">Margine:</span>
                            <span className={quote.margin_installation >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {formatCurrency(quote.margin_installation)} ({formatPercent(quote.margin_installation_percent)})
                            </span>
                        </div>
                    </div>
                    
                    {/* Servizi */}
                    <div className="space-y-1">
                        <div className="text-xs text-zinc-500 uppercase">Servizi</div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Costo:</span>
                            <span>{formatCurrency(quote.purchase_services)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Vendita:</span>
                            <span className="font-medium">{formatCurrency(quote.total_services)}</span>
                        </div>
                        <div className="flex justify-between pt-1 border-t">
                            <span className="text-zinc-500">Margine:</span>
                            <span className={quote.margin_services >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {formatCurrency(quote.margin_services)} ({formatPercent(quote.margin_services_percent)})
                            </span>
                        </div>
                    </div>
                    
                    {/* Totale */}
                    <div className="space-y-1 bg-white p-2 rounded-lg border">
                        <div className="text-xs text-zinc-500 uppercase font-semibold">TOTALE</div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Costo:</span>
                            <span className="font-medium">{formatCurrency(quote.total_purchase)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Vendita:</span>
                            <span className="font-semibold">{formatCurrency(quote.total_net)}</span>
                        </div>
                        <div className="flex justify-between pt-1 border-t">
                            <span className="text-zinc-600 font-medium">Margine:</span>
                            <span className={`font-bold ${quote.total_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(quote.total_margin)}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">%:</span>
                            <span className={`font-bold ${quote.total_margin_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatPercent(quote.total_margin_percent)}
                            </span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// Componente principale
export default function QuoteMaterialsCPQ({ 
    quote, 
    isEditable, 
    onUpdateLine, 
    onDeleteLine,
    onOpenCatalog,
    userRole = 'admin'
}) {
    // Determina se l'utente può vedere i margini
    const canViewMargins = useMemo(() => {
        return ['admin', 'commerciale'].includes(userRole);
    }, [userRole]);
    
    // Filtra solo materiali
    const materials = useMemo(() => {
        return (quote.lines || [])
            .map((line, index) => ({ ...line, originalIndex: index }))
            .filter(line => line.line_type === 'MATERIAL');
    }, [quote.lines]);
    
    const handleUpdate = async (originalIndex, data) => {
        try {
            await onUpdateLine(originalIndex, data);
        } catch (error) {
            toast.error('Errore nell\'aggiornamento');
        }
    };
    
    const handleDelete = async (originalIndex) => {
        if (window.confirm('Eliminare questo materiale?')) {
            try {
                await onDeleteLine(originalIndex);
                toast.success('Materiale eliminato');
            } catch (error) {
                toast.error('Errore nell\'eliminazione');
            }
        }
    };
    
    return (
        <div className="space-y-4">
            {/* Riepilogo Margini */}
            <MarginSummary quote={quote} canViewMargins={canViewMargins} />
            
            {/* Tabella Materiali */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-lg">Fornitura Materiale</CardTitle>
                        <CardDescription>
                            {materials.length} componenti • Totale cliente: {formatCurrency(quote.total_materials)}
                        </CardDescription>
                    </div>
                    {isEditable && (
                        <Button onClick={onOpenCatalog}>
                            Aggiungi da Catalogo
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    {materials.length === 0 ? (
                        <div className="text-center py-8 text-zinc-500">
                            <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>Nessun materiale aggiunto</p>
                            {isEditable && (
                                <Button variant="outline" className="mt-2" onClick={onOpenCatalog}>
                                    Seleziona dal Catalogo
                                </Button>
                            )}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-10"></TableHead>
                                    <TableHead>Componente</TableHead>
                                    <TableHead className="w-20">Qtà</TableHead>
                                    {canViewMargins && <TableHead className="text-right">Costo Acq.</TableHead>}
                                    <TableHead className="text-right">Prezzo Cliente</TableHead>
                                    <TableHead className="w-20">Sconto</TableHead>
                                    <TableHead className="text-right">Totale</TableHead>
                                    {canViewMargins && <TableHead>Margine</TableHead>}
                                    <TableHead className="w-24">Azioni</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {materials.map((line) => (
                                    <MaterialRow
                                        key={line.originalIndex}
                                        line={line}
                                        index={line.originalIndex}
                                        isEditable={isEditable}
                                        onUpdate={handleUpdate}
                                        onDelete={handleDelete}
                                        canViewMargins={canViewMargins}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
